import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { queryKeys, STALE_TIME } from '../../lib/queryClient';
import { apiService } from '../../services/api.service';
import { unwrapApiResponse } from '../../services/api/http-client.types';
import { listWorkspaceItems } from '../../services/api/workspaceItems.api';
import { FOLDER_WORD } from '../../theme/entityIcons';

type FolderPath = ReadonlyArray<{ readonly id: string; readonly name: string }>;

const ROOT_PATH: FolderPath = [];

/** The first page a mounted list of this folder is loading right now, if any. */
function listPageOneInFlight(
  queryClient: QueryClient,
  workspaceId: string,
  folderId: string,
): Promise<unknown> | undefined {
  return queryClient
    .getQueryCache()
    .findAll({
      queryKey: ['workspace-items', { workspaceId }, { parent: folderId }],
      predicate: (q) => q.state.fetchStatus === 'fetching' && q.state.data === undefined,
    })
    .find((q) => q.promise)?.promise;
}

async function fetchFolderPath(
  queryClient: QueryClient,
  workspaceId: string,
  folderId: string,
  signal: AbortSignal,
): Promise<FolderPath> {
  // Every subscription in this render commit starts its fetch before this
  // continues, so a folder list mounted alongside is already in flight.
  await Promise.resolve();
  const inFlight = listPageOneInFlight(queryClient, workspaceId, folderId);
  if (inFlight) {
    // That page 1 seeds this very key on arrival (fetchFolderPage).
    await inFlight;
    return (
      queryClient.getQueryData<FolderPath>(queryKeys.folderPath(workspaceId, folderId)) ?? ROOT_PATH
    );
  }
  const res = await listWorkspaceItems(apiService, { parent: folderId, limit: 1 }, signal);
  return (
    unwrapApiResponse(res, `Could not load the path to this ${FOLDER_WORD}`).folderPath ?? ROOT_PATH
  );
}

/**
 * The breadcrumb from the workspace root to `folderId` (null = the root, an
 * empty path with no request).
 *
 * With the folder's list mounted this costs nothing extra: the list's page 1
 * carries `folderPath` and seeds this cache (`fetchFolderPage`), and a
 * breadcrumb mounted in the same render waits on the list's request instead
 * of sending its own. With no list mounted (a move dialog, a header
 * elsewhere), it asks for one row of the folder and keeps only the path.
 */
export function useFolderPath(folderId: string | null) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.folderPath(workspaceId ?? '', folderId ?? ''),
    queryFn: ({ signal }) =>
      fetchFolderPath(queryClient, workspaceId ?? '', folderId ?? '', signal),
    enabled: !!workspaceId && folderId !== null,
    staleTime: STALE_TIME.ENTITY,
  });

  return { ...query, path: folderId === null ? ROOT_PATH : (query.data ?? ROOT_PATH) };
}
